import { clamp, lerp } from '@studio/utils/utils';
import { getTimingFunctionByKey } from './timing-functions';
/** Used to animate elements if the creative is paused */
export class AnimatorLite {
    get isPlaying() {
        return !this._paused;
    }
    constructor(target) {
        this.target = target;
        this._speed = 1;
        this._lastDrawTime = 0;
        this._time = 0;
        this._duration = 0.5;
        this._timingFunction = getTimingFunctionByKey('linear');
        this._paused = true;
        this._onCompletedCallbacks = [];
        this.onCompleted = (callback) => {
            this._onCompletedCallbacks.push(callback);
        };
        this._tick = (t) => {
            if (this._paused) {
                return;
            }
            // throttle to desired fps
            if (this._customFPSinterval && t - this._lastDrawTime < this._customFPSinterval) {
                this._requestId = requestAnimationFrame(this._tick);
                return;
            }
            this._lastDrawTime = t;
            if (!this._lastTimestamp) {
                this._lastTimestamp = t;
            }
            const timestampDiff = (t - this._lastTimestamp) / 1000;
            this._time += timestampDiff * this._speed;
            this._lastTimestamp = t;
            if (!this._updateTarget(this._time)) {
                this._requestId = requestAnimationFrame(this._tick);
            }
            else {
                this.pause();
            }
        };
    }
    static to(target, to, settings) {
        return new AnimatorLite(target).to(to, settings);
    }
    to(to, settings) {
        return this.fromTo(this.target, to, settings);
    }
    from(from, settings) {
        return this.fromTo(from, this.target, settings);
    }
    fromTo(from, to, settings) {
        this._applySettings(settings);
        this._fromValue = this._filterAnimatableProperties(from);
        this._toValue = this._filterAnimatableProperties(to);
        return this.restart().play();
    }
    play() {
        if (this._paused === true) {
            this._paused = false;
            this._requestId = requestAnimationFrame(this._tick);
        }
        return this;
    }
    pause() {
        this._paused = true;
        this._lastTimestamp = 0;
        this._cancelAnimation();
    }
    restart() {
        this._time = 0;
        return this;
    }
    revert() {
        this._speed *= -1;
        return this.play();
    }
    _applySettings(settings = {}) {
        if (settings.customFPSInterval !== undefined) {
            this._customFPSinterval = settings.customFPSInterval;
        }
        if (settings.duration !== undefined) {
            this._duration = settings.duration;
        }
        if (settings.timingFunction !== undefined) {
            this._timingFunction = getTimingFunctionByKey(settings.timingFunction);
        }
        this._onTick = settings.callback;
    }
    _updateTarget(time) {
        const progress = this._getCompletionRate(time);
        const timing = this._getTimingValue(progress);
        if (typeof this._toValue === 'object') {
            Object.keys(this._toValue).forEach(key => {
                const from = this._fromValue[key];
                const to = this._toValue[key];
                if (typeof from !== 'number') {
                    throw new Error(`Cannot animate property ${key}. Must be of type number`);
                }
                this.target[key] = lerp(from, to, timing);
            });
        }
        if (this._onTick) {
            this._onTick(this);
        }
        const isCompleted = this._speed < 0 ? progress <= 0 : progress >= 1;
        if (isCompleted) {
            for (const callback of this._onCompletedCallbacks) {
                callback();
            }
        }
        // Return true if animation is completed
        return isCompleted;
    }
    _getTimingValue(progress) {
        return this._timingFunction(progress);
    }
    /**
     * Get a value between 0 and 1 on how much progress on time have been
     * @param time
     */
    _getCompletionRate(time) {
        return this._duration > 0 ? clamp(time / this._duration, 0, 1) : 1;
    }
    _cancelAnimation() {
        if (this._requestId) {
            cancelAnimationFrame(this._requestId);
            this._requestId = undefined;
        }
    }
    _filterAnimatableProperties(obj) {
        const result = {};
        if (typeof obj === 'number') {
            result.value = obj;
        }
        if (typeof obj === 'object') {
            Object.keys(obj).forEach(key => {
                const value = obj[key];
                if (typeof value === 'number') {
                    result[key] = value;
                }
            });
        }
        return result;
    }
}
